/**
 * useOrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListLogic
 *
 * Pure logic hook — state machine, orbital bridge, actions.
 * Use the companion component for rendering.
 *
 * @generated from schema
 * @trait OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskList
 */

import { useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import { emitCascadeEvent, useOrbitalBridge } from '@/hooks/useOrbitalBridge';
import type { EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import type { EventResponse, OrbDocTutorialsBeginnerTaskManagerARTask, OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListEventPayloadMap } from '@app/shared';
import { useUIEvents, claimDelivery } from '@almadar/ui/hooks';
import { registerTraitSnapshot, type TraitStateSnapshot } from '@almadar/ui/lib';
import { createCommandSendPump } from '@almadar/ui/lib';
import type { BusEvent } from '@almadar/core';

// ============================================================================
// State Machine
// ============================================================================

export type OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListState =
  | 'loading'
  | 'ready';

export type OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListEvent =
  | 'INIT'
  | 'TasksLoaded'
  | 'TasksFailed'
  | 'COMPLETE'
  | 'DELETE'
  | 'RELOAD'
  | 'MOVED'
  | 'TASK_ADDED';

type OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListSlot = 'main';
interface OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListInternalState {
  machineState: OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListEvent | null;
  slotWriter: Partial<Record<OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListSlot, OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListEvent | null>>;
  loading: boolean;
  pendingEvent: OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<OrbDocTutorialsBeginnerTaskManagerARTask>;
}

type OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListAction =
  | { type: 'EVENT_SUCCESS'; event: OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListEvent; newState: string; data: Record<string, EntityRow[]>; payload?: EventPayload; fields?: Record<string, EventPayloadValue | undefined>; renderBearing?: boolean; renderWrite?: OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListSlot[]; renderClear?: OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListSlot[] }
  | { type: 'EVENT_ERROR'; error: string }
  | { type: 'RESET_ERROR' }
  | { type: 'START_EVENT'; event: OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListEvent; from: string }
  | { type: 'END_EVENT' }
  | { type: 'SET_FIELD'; field: keyof OrbDocTutorialsBeginnerTaskManagerARTask & string; value: EventPayloadValue | undefined }
  | { type: 'TICK'; updates: Record<string, EventPayloadValue | undefined>; collection: string };

function orbDocTutorialsBeginnerTaskManagerARDocEx1TaskListReducer(
  state: OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListInternalState,
  action: OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListAction
): OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListInternalState {
  switch (action.type) {
    case 'START_EVENT':
      return { ...state, loading: true, pendingEvent: action.event, pendingFrom: action.from };
    case 'END_EVENT':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null };
    case 'EVENT_SUCCESS':
      return {
        machineState: action.newState as OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListState,
        data: { ...state.data, ...action.data },
        lastPayload: action.renderBearing ? (action.payload ?? state.lastPayload) : state.lastPayload,
        lastEvent: action.renderBearing ? action.event : state.lastEvent,
        slotWriter: {
          ...state.slotWriter,
          ...Object.fromEntries((action.renderWrite ?? []).map((s) => [s, action.event] as const)),
          ...Object.fromEntries((action.renderClear ?? []).map((s) => [s, null] as const)),
        },
        loading: false,
        pendingEvent: null,
        pendingFrom: null,
        error: null,
        fields: { ...state.fields, ...(action.fields ?? {}) } as Partial<OrbDocTutorialsBeginnerTaskManagerARTask>,
      };
    case 'EVENT_ERROR':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null, error: action.error };
    case 'RESET_ERROR':
      return { ...state, error: null };
    case 'SET_FIELD': {
      // Back-compat for non-codegen consumers that still dispatch
      // SET_FIELD directly. Codegen folds field updates into
      // EVENT_SUCCESS so transitions commit in a single render.
      return {
        ...state,
        fields: { ...state.fields, [action.field]: action.value } as Partial<OrbDocTutorialsBeginnerTaskManagerARTask>,
      };
    }
    case 'TICK': {
      // Batch-update entity fields for tick effects. A [runtime]-linked
      // trait's row lives in the `fields` mirror (transitions write it,
      // the view reads it), so updates ALWAYS merge there; the
      // `data` collection row is updated too when one exists.
      const tickFields = { ...state.fields, ...action.updates } as Partial<OrbDocTutorialsBeginnerTaskManagerARTask>;
      const arr = state.data[action.collection];
      const target = arr?.[0];
      if (!target) return { ...state, fields: tickFields };
      const updated = { ...target, ...action.updates } as EntityRow;
      return {
        ...state,
        data: { ...state.data, [action.collection]: [updated, ...arr.slice(1)] },
        fields: tickFields,
      };
    }
    default:
      return state;
  }
}

const initialState: OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListInternalState = {
  machineState: 'loading',
  data: {},
  lastPayload: undefined,
  lastEvent: null,
  slotWriter: {},
  loading: false,
  pendingEvent: null,
  pendingFrom: null,
  error: null,
  fields: {},
};

// ============================================================================
// Hook
// ============================================================================

export interface OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
}

export interface OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListLogicReturn {
  state: OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListEvent | null;
  slotWriter: Partial<Record<OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListSlot, OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListEvent | null>>;
  loading: boolean;
  pendingEvent: OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<OrbDocTutorialsBeginnerTaskManagerARTask>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListEventPayloadMap]: (payload?: OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListEventPayloadMap[K]) => Promise<EventResponse> };
  config?: OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListConfig;
}

const __CASCADE_EVENTS = new Set<string>(['RELOAD', 'TASK_ADDED']);

const __LEG_EVENTS: ReadonlySet<string> = new Set(['COMPLETE', 'DELETE', 'INIT', 'RELOAD']);

export function useOrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListLogic(config?: Partial<OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListConfig>): OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListLogicReturn {
  const mergedConfig = config;

  // Orbital bridge: event bus + server communication
  const { sendEvent, eventBus } = useOrbitalBridge({ basePath: '/api/orb-doc-tutorials-beginner-task-manager-ar-doc-ex1-task-list', orbitalName: 'OrbDocTutorialsBeginnerTaskManagerAR', traitName: 'OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskList' });

  // Internal state with reducer
  const [internalState, dispatchReducer] = useReducer(orbDocTutorialsBeginnerTaskManagerARDocEx1TaskListReducer, initialState);
  const { machineState: state, data, lastPayload, lastEvent, slotWriter, loading, pendingEvent, pendingFrom, error, fields: _initialFields } = internalState;
  const fields = _initialFields;
  const internalStateRef = useRef(internalState);
  internalStateRef.current = internalState;

  const cascadeReceivedRef = useRef<TraitStateSnapshot['cascadeReceived']>([]);

  const machineStateRef = useRef(state);
  machineStateRef.current = state;

  const fieldsRef = useRef<EntityRow>({ ...fields });
  fieldsRef.current = { ...fields };
  const commandPump = useMemo(() => createCommandSendPump(), []);

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, _payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const entity: EntityRow[] = liveData['OrbDocTutorialsBeginnerTaskManagerARTask'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};

    if (fromState === 'loading' && event === 'INIT') {
      // loading → loading (on INIT)
      _newState = 'loading';
    } else if (fromState === 'loading' && event === 'TasksLoaded') {
      // loading → ready (on TasksLoaded)
        _emits.push(((_p) => () => { eventBus.emit('MOVED', _p, { trait: 'OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskList' }); emitCascadeEvent(eventBus, 'OrbDocTutorialsBeginnerTaskManagerAR', 'OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskList', { event: 'MOVED', payload: _p, source: { orbital: 'OrbDocTutorialsBeginnerTaskManagerAR', trait: 'OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskList' } }); })({ event: "TasksLoaded" as const }));
      _newState = 'ready';
    } else if (fromState === 'loading' && event === 'TasksFailed') {
      // loading → ready (on TasksFailed)
        _emits.push(((_p) => () => { eventBus.emit('MOVED', _p, { trait: 'OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskList' }); emitCascadeEvent(eventBus, 'OrbDocTutorialsBeginnerTaskManagerAR', 'OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskList', { event: 'MOVED', payload: _p, source: { orbital: 'OrbDocTutorialsBeginnerTaskManagerAR', trait: 'OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskList' } }); })({ event: "TasksFailed" as const }));
      _newState = 'ready';
    } else if (fromState === 'ready' && event === 'COMPLETE') {
      // ready → loading (on COMPLETE)
        eventBus.emit('MOVED', { event: "COMPLETE" as const }, { trait: 'OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskList' });
      _newState = 'loading';
    } else if (fromState === 'ready' && event === 'DELETE') {
      // ready → loading (on DELETE)
        eventBus.emit('MOVED', { event: "DELETE" as const }, { trait: 'OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskList' });
      _newState = 'loading';
    } else if (fromState === 'ready' && event === 'RELOAD') {
      // ready → loading (on RELOAD)
        eventBus.emit('MOVED', { event: "RELOAD" as const }, { trait: 'OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskList' });
      _newState = 'loading';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  // Used by dispatch to skip lastPayload/lastEvent updates for
  // transitions whose only effects are server-side or set/emit —
  // those don't change the rendered JSX, so the view should keep
  // the prior frame's payload until a render-bearing transition
  // (e.g. fetch.emit.success cascade) lands.
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListSlot[]; clear: OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListSlot[] } => {
    switch (fromState) {
      case 'loading':
        switch (event) {
          case 'INIT': return { write: ['main'] as OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListSlot[], clear: [] as OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListSlot[] };
          case 'TasksFailed': return { write: ['main'] as OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListSlot[], clear: [] as OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListSlot[] };
          case 'TasksLoaded': return { write: ['main'] as OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListSlot[], clear: [] as OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListSlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event through the orbital bridge.
   * Bridge emits lifecycle events on the bus, forwards to server.
   * Client effects are transpiled at compile time (Phase 4).
   */
  const dispatch = useCallback(async (event: string, __rawPayload?: EventPayload): Promise<EventResponse> => {
    const payload: EventPayload | undefined =
      __rawPayload !== undefined && ('entityId' in __rawPayload || 'id' in __rawPayload)
        ? __rawPayload
        : config?.entityId !== undefined
          ? { ...(__rawPayload ?? {}), entityId: config.entityId }
          : __rawPayload;
    const prevState = machineStateRef.current;
    // T9 optimistic dispatch: the fields mirror IS the [runtime] entity
    // row, so guard + to-state resolve locally on the same data the
    // server would read. Commit first, POST after; reconcile below.
    const liveData: Record<string, EntityRow[]> = { 'OrbDocTutorialsBeginnerTaskManagerARTask': [fieldsRef.current as EntityRow] };
    const _optimistic = executeTransitionEffects(prevState, event, liveData, payload);
    if (_optimistic.newState === undefined) {
      // Guard rejected or no transition defined — mirrors the server's
      // rejection, without the round trip.
      const errorMsg = `No transition defined for ${event} from ${prevState} (or guard rejected)`;
      dispatchReducer({ type: 'EVENT_ERROR', error: errorMsg });
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const _renderSlots = getRenderUiSlots(prevState, event);
    const _renderBearing = _renderSlots.write.length > 0 || _renderSlots.clear.length > 0;
    // Snapshot the touched fields' prior values for a reconcile rollback.
    const _snapshot: Record<string, EventPayloadValue | undefined> = {};
    for (const _k of Object.keys(_optimistic.fields)) { _snapshot[_k] = fieldsRef.current[_k]; }
    dispatchReducer({
      type: 'EVENT_SUCCESS',
      event: event as OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListEvent,
      newState: _optimistic.newState,
      data: {},
      payload,
      fields: _optimistic.fields,
      renderBearing: _renderBearing,
      renderWrite: _renderSlots.write,
      renderClear: _renderSlots.clear,
    });
    if (__CASCADE_EVENTS.has(event)) {
      cascadeReceivedRef.current.push({ event, ...(payload !== undefined ? { payload } : {}), timestamp: Date.now() });
    }
    machineStateRef.current = _optimistic.newState as typeof machineStateRef.current;
    fieldsRef.current = { ...fieldsRef.current, ..._optimistic.fields } as EntityRow;
    for (const _emit of _optimistic.emits) _emit();

    if (__LEG_EVENTS.has(event)) {
    // Server leg: ordered by the pump, never blocking the drain.
    commandPump.enqueue(async () => {
      try {
        const response = await sendEvent(event, payload, prevState);
        if (response.success) {
          // Agreement — merge any fetch/persist results the local commit
          // couldn't supply (same reducer action, deferred leg).
          if (response.data && Object.keys(response.data).length > 0) {
            dispatchReducer({
              type: 'EVENT_SUCCESS',
              event: event as OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListEvent,
              newState: machineStateRef.current,
              data: response.data,
              renderBearing: false,
            });
          }
        } else {
          // Rejection or state disagreement — roll back to the
          // pre-dispatch snapshot; the server stays authoritative.
          dispatchReducer({
            type: 'EVENT_SUCCESS',
            event: event as OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListEvent,
            newState: prevState,
            data: {},
            fields: _snapshot,
            renderBearing: false,
          });
          machineStateRef.current = prevState;
          fieldsRef.current = { ...fieldsRef.current, ..._snapshot } as EntityRow;
          dispatchReducer({ type: 'EVENT_ERROR', error: response.error ?? `server state mismatch: expected ${_optimistic.newState}, got ${response.states?.['OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskList'] ?? '(unchanged)'}` });
        }
      } catch (err) {
        // Pump isolates the rejection; surface it without rolling
        // back — local state may simply be ahead of a reconnecting server.
        dispatchReducer({ type: 'EVENT_ERROR', error: err instanceof Error ? err.message : 'Network error' });
      }
    });
    }
    return { success: true, transitioned: true, states: { 'OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskList': _optimistic.newState }, emittedEvents: [] } as EventResponse;
  }, [sendEvent, executeTransitionEffects, commandPump, config?.entityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListEventPayloadMap]: (payload?: OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
    'TasksLoaded': (payload?: OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListEventPayloadMap['TasksLoaded']) => dispatch('TasksLoaded', payload),
    'TasksFailed': (payload?: OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListEventPayloadMap['TasksFailed']) => dispatch('TasksFailed', payload),
    'COMPLETE': (payload?: OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListEventPayloadMap['COMPLETE']) => dispatch('COMPLETE', payload),
    'DELETE': (payload?: OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListEventPayloadMap['DELETE']) => dispatch('DELETE', payload),
    'RELOAD': () => dispatch('RELOAD'),
    'MOVED': (payload?: OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskListEventPayloadMap['MOVED']) => dispatch('MOVED', payload),
  };

  useUIEvents(enqueueEvent, 'OrbDocTutorialsBeginnerTaskManagerAR.OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskList', ['INIT', 'TasksLoaded', 'TasksFailed', 'COMPLETE', 'DELETE', 'RELOAD'] as const, eventBus);

  // Verifier snapshot registration (VG Foundation 1)
  useEffect(() => {
    const unregister = registerTraitSnapshot('OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskList', (): TraitStateSnapshot => ({
      traitName: 'OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskList',
      currentState: internalStateRef.current.machineState,
      states: ['loading', 'ready'],
      events: ['INIT', 'TasksLoaded', 'TasksFailed', 'COMPLETE', 'DELETE', 'RELOAD', 'MOVED'],
      data: internalStateRef.current.data,
      lastPayload: internalStateRef.current.lastPayload,
      lastEventDispatched: internalStateRef.current.lastEvent !== null
        ? {
            event: internalStateRef.current.lastEvent,
            ...(internalStateRef.current.lastPayload !== undefined
              ? { payload: internalStateRef.current.lastPayload }
              : {}),
            timestamp: Date.now(),
          }
        : undefined,
      cascadeReceived: cascadeReceivedRef.current,
    }));
    return unregister;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cross-trait event listeners (gap #13: qualified bus keys)
  useEffect(() => {
    // Listen for 'TASK_ADDED' (from OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskForm) → trigger 'RELOAD'
    const unsub_task_added_from_orbdoctutorialsbeginnertaskmanagerardocex1taskform_to_reload = eventBus.on('UI:OrbDocTutorialsBeginnerTaskManagerAR.OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskForm.TASK_ADDED', (event: BusEvent) => {
      if (!claimDelivery(event.source, 'OrbDocTutorialsBeginnerTaskManagerAR.OrbDocTutorialsBeginnerTaskManagerARDocEx1TaskList', 'RELOAD')) return;
      enqueueEvent('RELOAD', event.payload);
    });
    return () => {
      if (typeof unsub_task_added_from_orbdoctutorialsbeginnertaskmanagerardocex1taskform_to_reload === 'function') unsub_task_added_from_orbdoctutorialsbeginnertaskmanagerardocex1taskform_to_reload();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventBus, dispatch]);

  return {
    state,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading,
    pendingEvent,
    pendingFrom,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
